/*
  Derived figures for a report's videos, plus the small parsers the detail pane
  needs for the comma- and pipe-separated fields the API returns.

  MatchingVideos keys a video as VideoId, the playback summary keys it as
  videoId, and the two disagree on type — hence String() on both sides of every
  join.
*/

// MatchingVideos arrives either as a JSON string or already parsed.
export const parseMatchingVideos = (matchingVideos) => {
  if (!matchingVideos) return []
  if (Array.isArray(matchingVideos)) return matchingVideos
  try {
    return JSON.parse(matchingVideos)
  } catch {
    // Warn rather than swallow: an unparseable payload otherwise renders as an
    // ordinary "0 videos", indistinguishable from a genuine zero.
    console.warn("Could not parse MatchingVideos", matchingVideos)
    return []
  }
}

export const parseCsvList = (value) =>
  value
    ? value
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean)
    : []

export const parsePhysicians = (matchingPhysicians) => {
  if (!matchingPhysicians) return []
  const segments = matchingPhysicians
    .split(/\s*\|\|\s*|\s*\|\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean)

  const seen = new Set()
  const physicians = []
  for (const segment of segments) {
    const npiMatch = segment.match(/^(\d{6,})\s*(?:,\s*(.*))?$/)
    if (npiMatch) {
      const npi = npiMatch[1].trim()
      const name = (npiMatch[2] || "").trim()
      const key = `${npi}|${name}`
      if (seen.has(key)) continue
      seen.add(key)
      physicians.push({ npi, name, specialty: "" })
    } else if (physicians.length) {
      physicians[physicians.length - 1].specialty = segment
    }
  }
  return physicians
}

export const parseSpecialtyChips = (specialty) =>
  specialty
    ? [
        ...new Set(
          specialty
            .split(/[;,]/)
            .map((part) => part.trim())
            .filter(Boolean)
        ),
      ]
    : []

export const buildPlaybackIndex = (playbackSummary) =>
  (playbackSummary || []).reduce((byId, playback) => {
    byId[String(playback.videoId)] = playback
    return byId
  }, {})

/*
  How far the patient got with one video.

  A null maxPercent means the video was never played, which is not the same as
  having played it and reached 0% — the second is real engagement data, so it
  keeps a normal title and an empty bar rather than the muted "not viewed"
  treatment.

  maxPercent originates from ViewMedica's "50%" strings, so it is clamped; a
  fractional or out-of-range value would otherwise size the bar wrongly.
*/
export const getVideoProgress = (playback) => {
  const raw = playback?.maxPercent ?? null
  const isViewed = raw !== null
  const percent = isViewed ? Math.min(100, Math.max(0, Math.round(raw))) : 0
  // The API keeps the name watchCount, but it is the number of plays: a new
  // play starts after the video finished, or on a jump back to the start once
  // the previous play passed 90%. Earlier rewinds stay in the same play.
  const watchCount = playback?.watchCount ?? 0
  const completedPlayCount = playback?.completedPlayCount ?? 0

  return {
    isViewed,
    percent,
    // video_ended can fire without a 100% milestone, so `completed` counts too.
    isComplete: percent >= 100 || playback?.completed === true,
    watchCount,
    completedPlayCount,
  }
}

/*
  Coverage across a report's videos: how many were opened, and how far the
  patient got in the ones they did open.

  Iterates the assigned videos and looks playback up, never the reverse — the
  playback summary can still hold a video since removed from the report, which
  counted the other way around would report "6 of 5".

  The average spans viewed videos only. Averaging over all of them (unviewed as
  0%) conflates "never opened" with "abandoned immediately", and makes the figure
  drop when a video is added to a report — an engagement regression that never
  happened. The "N of M" half already carries coverage; the percentage describes
  depth.
*/
export const summariseVideos = (videos, playbackByVideoId) => {
  const viewed = videos
    .map((video) => getVideoProgress(playbackByVideoId[String(video?.VideoId)]))
    .filter((progress) => progress.isViewed)

  const averagePercent = viewed.length
    ? Math.round(
        viewed.reduce((sum, progress) => sum + progress.percent, 0) /
          viewed.length
      )
    : null

  return { total: videos.length, viewedCount: viewed.length, averagePercent }
}
